import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, RefreshCw } from 'lucide-react';
import type { DeskDetail, DeskOrigin, DeskOriginBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { useAuthUser } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/input';
import { Badge, Dialog } from '@/components/ui/misc';
import { errorMessage } from '@/features/desks/desks-queries';

/** Selo da origem — o mesmo texto no cabeçalho da mesa e na seção. */
export function DeskOriginBadge({ origin }: { origin: DeskOrigin }) {
  return origin === 'tiflux' ? (
    <Badge variant="info">
      <RefreshCw className="size-3" />
      Espelhada do TiFlux
    </Badge>
  ) : (
    <Badge variant="success">Operada no ERP</Badge>
  );
}

/**
 * Origem da mesa (spec 011): quem manda nos tickets dela — o espelho do TiFlux
 * ou o ERP. É a chave da migração mesa a mesa, por isso a troca é admin-only
 * (a API responde 403 aos demais), vai por rota própria e sempre passa por um
 * diálogo que diz o que muda. A volta ao TiFlux exige marcar o aviso de
 * sobrescrita: ela desfaz, de propósito, o que foi editado aqui no intervalo.
 */
export function DeskOriginSection({ desk }: { desk: DeskDetail }) {
  const user = useAuthUser();
  const isAdmin = user?.role === 'admin';
  const [confirmOpen, setConfirmOpen] = useState(false);
  const target: DeskOrigin = desk.origin === 'tiflux' ? 'erp' : 'tiflux';

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold">Origem dos tickets</h2>
          <DeskOriginBadge origin={desk.origin} />
        </div>
        {isAdmin ? (
          <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
            <ArrowLeftRight className="size-3.5" />
            {target === 'erp' ? 'Operar no ERP' : 'Devolver ao TiFlux'}
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">
        {desk.origin === 'tiflux'
          ? 'O TiFlux é quem manda: a cada sincronização o espelho atualiza esta mesa e os tickets dela, e o ERP só mostra — edições são feitas no TiFlux.'
          : 'O ERP é quem manda: o espelho do TiFlux não toca nesta mesa nem nos tickets dela, e o que mudar lá não chega aqui.'}
      </p>

      {confirmOpen ? (
        <ConfirmOriginDialog desk={desk} target={target} onClose={() => setConfirmOpen(false)} />
      ) : null}
    </section>
  );
}

function ConfirmOriginDialog({
  desk,
  target,
  onClose,
}: {
  desk: DeskDetail;
  target: DeskOrigin;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [acknowledged, setAcknowledged] = useState(false);
  const toErp = target === 'erp';

  const change = useMutation({
    mutationFn: async (body: DeskOriginBody) => {
      const res = await api<DeskDetail>(`/desks/${desk.id}/origin`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      // a mesa E os tickets dela: o selo e os controles de edição do detalhe,
      // do quadro e da lista dependem da origem
      void queryClient.invalidateQueries({ queryKey: ['erp', 'desks'] });
      void queryClient.invalidateQueries({ queryKey: ['erp', 'tickets'] });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={
        toErp ? `Operar "${desk.displayName}" no ERP` : `Devolver "${desk.displayName}" ao TiFlux`
      }
    >
      {toErp ? (
        <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
          <li>
            O espelho do TiFlux para de atualizar esta mesa e os tickets dela — inclusive nome,
            estágios e prioridades, que passam a ser configurados aqui.
          </li>
          <li>
            Os tickets passam a ser editados no ERP. O que mudar no TiFlux a partir de agora não
            chega mais.
          </li>
          <li>Nada volta para o TiFlux: o ERP não escreve lá.</li>
          <li>A troca fica registrada com o seu usuário e o horário, e pode ser desfeita.</li>
        </ul>
      ) : (
        <div className="space-y-3">
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
            <li>
              Na próxima sincronização o espelho reescreve os tickets desta mesa com o estado do
              TiFlux, e volta a atualizar nome, estágios e prioridades.
            </li>
            <li className="font-medium text-destructive">
              As edições feitas no ERP desde a troca serão sobrescritas.
            </li>
            <li>
              Comentários e apontamentos lançados aqui continuam visíveis, mas não vão para o
              TiFlux. Tickets criados no ERP nesta mesa ficam somente leitura.
            </li>
          </ul>
          <div className="flex items-start gap-2">
            <Checkbox
              id="origin-ack"
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(v === true)}
            />
            <Label htmlFor="origin-ack" className="cursor-pointer text-sm font-normal">
              Entendo que as edições feitas no ERP nesta mesa serão sobrescritas pelo TiFlux.
            </Label>
          </div>
        </div>
      )}

      {change.error ? (
        <p className="mt-3 text-sm text-destructive">{errorMessage(change.error)}</p>
      ) : null}
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          type="button"
          size="sm"
          variant={toErp ? 'default' : 'destructive'}
          disabled={change.isPending || (!toErp && !acknowledged)}
          onClick={() => change.mutate({ origin: target })}
        >
          {change.isPending ? 'Salvando…' : toErp ? 'Operar no ERP' : 'Devolver ao TiFlux'}
        </Button>
      </div>
    </Dialog>
  );
}
